/** Display-only metadata. Positions and combat authority never come from the browser. */
export const DEFAULT_CAMERA_ZOOM = 2;
export const BASE_CAMERA_PIXELS_PER_METRE = 18;
export const MAX_VIEWPORT_CSS_PX = 4096;
export const VIEWPORT_AOI_SLACK_M = 16;

export interface ViewportReport {
  readonly cssWidth: number;
  readonly cssHeight: number;
  readonly zoom: number;
}
export interface ViewportHalfExtents {
  readonly halfWidthM: number;
  readonly halfHeightM: number;
}

/** Validate the entire untrusted report before converting CSS pixels into metres. */
export function resolveViewportReport(value: unknown): ViewportHalfExtents {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) throw new Error('invalid viewport report');
  const report = value as Record<string, unknown>;
  const { cssWidth, cssHeight, zoom } = report;
  if (typeof cssWidth !== 'number' || !Number.isFinite(cssWidth) || cssWidth <= 0 || cssWidth > MAX_VIEWPORT_CSS_PX
    || typeof cssHeight !== 'number' || !Number.isFinite(cssHeight) || cssHeight <= 0 || cssHeight > MAX_VIEWPORT_CSS_PX
    || typeof zoom !== 'number' || !Number.isInteger(zoom) || zoom < 1 || zoom > 5) {
    throw new Error('invalid viewport dimensions or zoom');
  }
  return {
    halfWidthM: cssWidth / (2 * BASE_CAMERA_PIXELS_PER_METRE * zoom),
    halfHeightM: cssHeight / (2 * BASE_CAMERA_PIXELS_PER_METRE * zoom),
  };
}

/**
 * The browser clamps its camera inward by up to a full half-extent at a world
 * edge. AOI is still queried around the authoritative player, so reserve a
 * whole extra half-extent before adding replication slack.
 */
export function viewportAoiRadiusM(size: ViewportHalfExtents, baselineRadiusM: number): number {
  if (!(baselineRadiusM > 0) || Number.isNaN(baselineRadiusM)
    || !Number.isFinite(size.halfWidthM) || size.halfWidthM <= 0 || size.halfWidthM > 256
    || !Number.isFinite(size.halfHeightM) || size.halfHeightM <= 0 || size.halfHeightM > 256) {
    throw new Error('invalid viewport AOI geometry');
  }
  return Math.max(baselineRadiusM, 2 * Math.hypot(size.halfWidthM, size.halfHeightM) + VIEWPORT_AOI_SLACK_M);
}
